//*****************************************************
// RevenueCat public API keys + key selection.
//
// Single home for every RevenueCat PUBLIC key (safe to ship in clients) and the
// environment/platform selection logic, shared by the web SDK setup
// (`@um/ui` platformWeb), the native SDK setup (uniqueminds platformNative),
// and the backend's REST calls (offerings resolution, Stripe receipts).
//
// Secret keys (`sk_…`) are NOT here — they live in backend secrets
// (`REVENUECAT_SECRET_API_KEY`).
//*****************************************************

/** Test-store public keys (prod / staging RevenueCat projects). */
export const REVENUECAT_TEST_API_KEY_PROD = "test_YVfWpUdzehJSUrFUcAVEJXGhIBI";
export const REVENUECAT_TEST_API_KEY_STAGING =
  "test_NaLQWyhEwQPsphfMZfKbsXteecm";

/** Web Billing public keys. */
export const REVENUECAT_WEB_BILLING_API_KEY_PROD =
  "rcb_gswVuhdnYbnXfByqUDDeqRTtxTXg";
export const REVENUECAT_WEB_BILLING_API_KEY_PROD_SANDBOX =
  "rcb_sb_ywnawyopdssSCefDuOaFEgefu";
export const REVENUECAT_WEB_BILLING_API_KEY_STAGING_SANDBOX =
  "rcb_sb_oXtwaNtuYFDzZijaZdagerJsB";

/** Stripe-app public keys (receipts API). */
export const REVENUECAT_STRIPE_API_KEY_PROD =
  "strp_InSyXdTyALtOpnQfTiZPvsVkDRm";
export const REVENUECAT_STRIPE_API_KEY_PROD_SANDBOX =
  "strp_sb_EUuFRjzRTaWKlFPaRtWaOhto";
/** Staging-project Stripe sandbox key (used by the backend emulator). */
export const REVENUECAT_STRIPE_API_KEY_STAGING_SANDBOX =
  "strp_sb_qkZZAJkmSbrlISOcCzXQbpVt";

/** Native store public keys (prod RevenueCat project). */
export const REVENUECAT_Apple_API_KEY = "appl_nbbaNMsyJMzyjoKJNcPsvWqOuZO";
export const REVENUECAT_Google_API_KEY = "goog_qQwKMbmwtPiyCRhvVQcUlzCZMeZ";

/** Client platform a RevenueCat public key is selected for. */
export type RevenueCatClientPlatform = "web" | "ios" | "android";

/**
 * Normalizes a free-form platform string (e.g. `Capacitor.getPlatform()`, a
 * query param) to a {@link RevenueCatClientPlatform}, defaulting to `web`.
 */
export function asRevenueCatClientPlatform(
  value: string | null | undefined
): RevenueCatClientPlatform {
  return value === "ios" || value === "android" ? value : "web";
}

/**
 * Selects the RevenueCat public API key for a platform + environment. This is
 * the one place the key-per-environment rules live:
 *
 * - Non-production (staging builds, emulator, backend staging project) always
 *   uses the **staging project's test-store key**, regardless of platform.
 * - Production iOS / Android use the Apple / Google store keys.
 * - Production web uses the Web Billing key — or its **sandbox** variant when
 *   `prodSandbox` is set (local dev builds pointed at the prod Firebase
 *   project, i.e. `VITE_FIREBASE_ENV === "prod"`).
 */
export function getRevenueCatPublicApiKey(options: {
  platform: RevenueCatClientPlatform;
  production: boolean;
  /** Production web only: use the Web Billing sandbox key (dev-against-prod builds). */
  prodSandbox?: boolean;
}): string {
  const { platform, production, prodSandbox } = options;
  if (!production) {
    return REVENUECAT_TEST_API_KEY_STAGING;
  }
  switch (platform) {
    case "ios":
      return REVENUECAT_Apple_API_KEY;
    case "android":
      return REVENUECAT_Google_API_KEY;
    case "web":
      return prodSandbox
        ? REVENUECAT_WEB_BILLING_API_KEY_PROD_SANDBOX
        : REVENUECAT_WEB_BILLING_API_KEY_PROD;
  }
}
